import type { DevActionStep, TaskDevServers } from './types/factory';

/** SF-T-209: dev servers on a task's worktree, as the hub and the browser both need them. */

/** A task's dev servers may sit idle this long by default, unless the project or the task says otherwise. */
export const DEV_IDLE_MINUTES_DEFAULT = 120;
/** The idle times people can choose, in minutes: 5 minutes to a week. */
export const DEV_IDLE_MINUTES_MIN = 5;
export const DEV_IDLE_MINUTES_MAX = 7 * 24 * 60;

/** A valid idle time in minutes, or null when it isn't one. */
export function cleanDevIdleMinutes(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value)) return null;
  return value >= DEV_IDLE_MINUTES_MIN && value <= DEV_IDLE_MINUTES_MAX ? value : null;
}

/** "2 hours", "90 minutes", "1 day": an idle time for a sentence. */
export function devIdleText(minutes: number): string {
  const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
  if (minutes % 1440 === 0) return plural(minutes / 1440, 'day');
  if (minutes % 60 === 0) return plural(minutes / 60, 'hour');
  return plural(minutes, 'minute');
}

/**
 * When a worktree's dev servers count as idle since: the latest of its agent's last turn ending or message
 * (`agentActiveAt`), someone starting them from the hub (`devStartedAt`), and the run starting or ending.
 * Null when none is known.
 */
export function devActiveSince(times: Array<string | number | Date | null | undefined>): number | null {
  const ms = times
    .map((t) => (t === null || t === undefined ? NaN : t instanceof Date ? t.getTime() : typeof t === 'number' ? t : Date.parse(t)))
    .filter((t) => Number.isFinite(t));
  return ms.length ? Math.max(...ms) : null;
}

/** True when servers active since `since` have been idle longer than `idleMinutes` at `now`. */
export function devServersIdle(since: number | null, idleMinutes: number, now: number): boolean {
  return since !== null && now - since >= idleMinutes * 60_000;
}

/**
 * SF-T-231: once the machine says a Start, Stop or Tidy finished, how long the page waits for a report that shows
 * it (servers listening, gone, or the worktree gone) before it says it hasn't (machines report each minute, and
 * straight after such a command).
 */
export const DEV_REPORT_WAIT_MS = 3 * 60_000;
/** A report this long after the command finished that still doesn't show it: it didn't take. */
const DEV_LATE_REPORT_MS = 60_000;
/** After this long, an action whose result never showed is old news: the panel just says how they stand. */
const DEV_STUCK_SHOWN_MS = 30 * 60_000;

export type DevStepKey = 'asked' | DevActionStep | 'confirm';
export type DevStepState = 'done' | 'current' | 'todo' | 'failed';
/** The actions whose progress shows (an idle stop is shown as a Stop). */
export type DevProgressAction = 'start' | 'stop' | 'tidy';

export interface DevProgress {
  action: DevProgressAction;
  /** On the machine; done there and waiting for a report that shows it; or not shown in time. */
  phase: 'working' | 'awaiting' | 'stuck';
  steps: Array<{ key: DevStepKey; label: string; state: DevStepState }>;
}

/** Each action's steps after Sent, the step the machine starts on, and the last one: a report that shows it. */
const PLANS: Record<DevProgressAction, { keys: (remake: boolean) => DevActionStep[]; first: (remake: boolean) => DevActionStep; labels: Partial<Record<DevStepKey, string>> }> = {
  start: {
    keys: (remake) => (remake ? ['remake', 'setup', 'servers'] : ['servers']),
    first: (remake) => (remake ? 'remake' : 'servers'),
    labels: { remake: 'Worktree', setup: 'Setup', servers: 'Servers', confirm: 'Listening' },
  },
  stop: { keys: () => ['servers'], first: () => 'servers', labels: { servers: 'Stopping', confirm: 'Stopped' } },
  tidy: {
    keys: () => ['servers', 'database', 'worktree'],
    first: () => 'servers',
    labels: { servers: 'Servers', database: 'Database', worktree: 'Worktree', confirm: 'Gone' },
  },
};

/** Whether the machine's latest report shows the action done: servers listening, none listening, worktree gone. */
function confirmed(action: DevProgressAction, d: Pick<TaskDevServers, 'running' | 'exists'>): boolean {
  return action === 'start' ? d.running : action === 'stop' ? !d.running : !d.exists;
}

/** The newest action as one whose progress shows, or null. */
export function devProgressAction(d: Pick<TaskDevServers, 'action'>): DevProgressAction | null {
  const a = d.action?.action;
  return a === 'start' || a === 'tidy' ? a : a === 'stop' || a === 'idle_stop' ? 'stop' : null;
}

/**
 * SF-T-231: how far the newest Start, Stop (or idle stop) or Tidy of a task's dev servers has got: sent to the
 * machine, its steps there (from the machine's progress), then a machine report that shows it. Null when there's
 * none on its way: the report shows it done, it failed, or it's long over.
 */
export function devProgress(d: Pick<TaskDevServers, 'action' | 'running' | 'exists' | 'reported_at'>, now: number): DevProgress | null {
  const a = d.action;
  const action = devProgressAction(d);
  if (!a || !action || a.status === 'failed') return null;
  const plan = PLANS[action];
  const remake = action === 'start' && a.remake === true;
  const keys: DevStepKey[] = ['asked', ...plan.keys(remake), 'confirm'];
  const at = (current: DevStepKey, state: DevStepState = 'current') => {
    const i = keys.indexOf(current);
    return keys.map((key, n) => ({
      key,
      label: key === 'asked' ? 'Sent' : (plan.labels[key] ?? key),
      state: n < i ? ('done' as const) : n === i ? state : ('todo' as const),
    }));
  };
  if (a.status === 'running') {
    const step = a.step && keys.includes(a.step) ? a.step : plan.first(remake);
    return { action, phase: 'working', steps: at(step) };
  }
  if (confirmed(action, d)) return null;
  const finished = Date.parse(a.at);
  if (!Number.isFinite(finished) || now - finished >= DEV_STUCK_SHOWN_MS) return null;
  const reported = d.reported_at ? Date.parse(d.reported_at) : NaN;
  const late = Number.isFinite(reported) && reported - finished >= DEV_LATE_REPORT_MS;
  if (late || now - finished >= DEV_REPORT_WAIT_MS) return { action, phase: 'stuck', steps: at('confirm', 'failed') };
  return { action, phase: 'awaiting', steps: at('confirm') };
}

/** SF-T-231: an action still on its way (on the machine, or waiting for its report): the page looks again. */
export function devProgressPending(d: Pick<TaskDevServers, 'action' | 'running' | 'exists' | 'reported_at'>, now: number): boolean {
  const phase = devProgress(d, now)?.phase;
  return phase === 'working' || phase === 'awaiting';
}
